"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ownerKeyOf = exports.StorageMeterSchema = exports.VideoAssignmentSchema = exports.VideoHighlightSchema = exports.VideoPageMarkSchema = exports.VideoAssigneeSchema = exports.InvalidTransitionError = exports.VIDEO_TASK_EVENTS = exports.VIDEO_TASK_STATUSES = void 0;
exports.transition = transition;
exports.visibleModules = visibleModules;
const zod_1 = require("zod");
/**
 * Video Tasks — a RubyTill admin assigns a topic to a tenant user, who records it
 * in their own store. Spec: docs/superpowers/specs/2026-09-29-video-tasks-design.md
 *
 * Identity is `assignee.userId`: the users-table id, which IS the Cognito `sub`
 * and IS the POS `employeeId`. POS tokens carry no email, so email can never be
 * the match key.
 */
exports.VIDEO_TASK_STATUSES = [
    'draft', 'assigned', 'in_progress', 'submitted', 'changes_requested', 'approved', 'cancelled',
];
exports.VIDEO_TASK_EVENTS = ['assign', 'start', 'submit', 'approve', 'request_changes', 'cancel'];
class InvalidTransitionError extends Error {
    constructor(from, event) {
        super(`cannot ${event} a task that is ${from}`);
        this.from = from;
        this.event = event;
        this.name = 'InvalidTransitionError';
    }
}
exports.InvalidTransitionError = InvalidTransitionError;
const EDGES = {
    draft: { assign: 'assigned', cancel: 'cancelled' },
    assigned: { assign: 'assigned', start: 'in_progress', cancel: 'cancelled' },
    // assign from in_progress / changes_requested = reassign (spec §7: "assignee not
    // found, admin can reassign"). The old assignee's in-flight submit then 403s.
    in_progress: { assign: 'assigned', start: 'in_progress', submit: 'submitted', cancel: 'cancelled' },
    submitted: { approve: 'approved', request_changes: 'changes_requested', cancel: 'cancelled' },
    changes_requested: { assign: 'assigned', start: 'in_progress', cancel: 'cancelled' },
    approved: {},
    cancelled: {},
};
/** The only place a task's status may change. Throws on an illegal edge. */
function transition(from, event) {
    const to = EDGES[from]?.[event];
    if (!to)
        throw new InvalidTransitionError(from, event);
    return to;
}
exports.VideoAssigneeSchema = zod_1.z.object({
    tenantId: zod_1.z.string(),
    userId: zod_1.z.string(),
    email: zod_1.z.string().default(''),
    name: zod_1.z.string().default(''),
});
exports.VideoPageMarkSchema = zod_1.z.object({ atMs: zod_1.z.number().int().min(0), route: zod_1.z.string(), label: zod_1.z.string().default('') });
exports.VideoHighlightSchema = zod_1.z.object({ atMs: zod_1.z.number().int().min(0) });
exports.VideoAssignmentSchema = zod_1.z.object({
    assignmentId: zod_1.z.string(),
    title: zod_1.z.string().min(1),
    brief: zod_1.z.string().default(''),
    keyPoints: zod_1.z.array(zod_1.z.string()).default([]),
    surface: zod_1.z.enum(['platform-app', 'pos']),
    target: zod_1.z.object({ moduleId: zod_1.z.string(), chapterId: zod_1.z.string().optional() }).optional(),
    dueAt: zod_1.z.number().int().optional(),
    assignee: exports.VideoAssigneeSchema.optional(),
    rewardTokens: zod_1.z.number().int().nonnegative().default(0),
    rewardStatus: zod_1.z.enum(['none', 'offered']).default('none'),
    status: zod_1.z.enum(exports.VIDEO_TASK_STATUSES).default('draft'),
    tutorialId: zod_1.z.string(),
    submission: zod_1.z.object({
        versionNo: zod_1.z.number().int().min(1),
        sessionKey: zod_1.z.string(),
        durationMs: zod_1.z.number().int().min(0),
        sizeBytes: zod_1.z.number().int().min(0),
        pages: zod_1.z.array(exports.VideoPageMarkSchema).default([]),
        highlights: zod_1.z.array(exports.VideoHighlightSchema).default([]),
        submittedAt: zod_1.z.number().int(),
    }).optional(),
    reviewNote: zod_1.z.string().default(''),
    history: zod_1.z.array(zod_1.z.object({
        at: zod_1.z.number().int(), by: zod_1.z.string(), from: zod_1.z.string(), to: zod_1.z.string(), note: zod_1.z.string().default(''),
    })).default([]),
    createdAt: zod_1.z.number().int(),
    updatedAt: zod_1.z.number().int(),
    createdBy: zod_1.z.string(),
});
const TENANT_AUDIENCES = new Set(['customer', 'employee']);
/**
 * What a tenant's Learn page may show. `userId` is accepted and unused so
 * per-user rules can be added later without changing any caller.
 */
function visibleModules(modules, enabledPackages, _userId) {
    const have = new Set(enabledPackages);
    return modules.filter((m) => m.status === 'published'
        && m.audience.some((a) => TENANT_AUDIENCES.has(a))
        && (m.packageIds.length === 0 || m.packageIds.some((p) => have.has(p))));
}
exports.StorageMeterSchema = zod_1.z.object({
    bytes: zod_1.z.number().default(0),
    files: zod_1.z.number().default(0),
    videos: zod_1.z.number().default(0),
});
/** Owner segment for storage meter keys and S3 prefixes. */
const ownerKeyOf = (tenantId) => (tenantId ? `TENANT#${tenantId}` : 'GLOBAL');
exports.ownerKeyOf = ownerKeyOf;
//# sourceMappingURL=videoTasks.js.map